import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import * as Yup from 'yup'

import { DateHelper, DatePicker, Select } from '@loadsmart/loadsmart-ui'
import { type Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import { Divider, Drawer, Field, Icon, Layout, Text } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { type OpenDockStopAppointment, type OpenDockWarehouseDetails } from '../../domain/Shipment'
import {
  formatScheduledAt,
  getStartTimesFromDocks,
} from '../../presenters/openDockAppointmentPresenter'
import { useGetOpenDockWarehouseAvailability } from '../hooks/useGetOpenDockWarehouseAvailability'
import { RectangleSkeleton } from '../OpenDockAppointmentDrawer/RectangleSkeleton'
import { ScheduleTimeList } from '../ScheduleTimeList/ScheduleTimeList'
import { OpenDockAppointmentSummary } from './OpenDockAppointmentSummary'

const Schema = Yup.object().shape({
  dock: Yup.object()
    .shape({ label: Yup.string().required(), value: Yup.string().required() })
    .nullable()
    .required(),
  appointmentTime: Yup.string().nullable().required('Required'),
})

const DatePickerWrapper = styled.div`
  [role='menubar'] {
    div:nth-child(1) {
      flex: 1;
    }
  }
`

export function Reschedule({
  onReschedule,
  onBack,
  stopAppointment,
  warehouseDetails,
  facilityName,
  facilityTimezone,
  shipmentUUID,
  stopIndex,
}: {
  onReschedule: () => void
  onBack: () => void
  stopAppointment?: OpenDockStopAppointment
  warehouseDetails?: OpenDockWarehouseDetails
  facilityName?: string
  facilityTimezone?: string
  shipmentUUID: string
  stopIndex: number
}) {
  const [appointmentDate, setAppointmentDate] = useState<string | null>(DateHelper().toString())

  const {
    warehouseAvailability: docksAvailability,
    fetchOpenDockWarehouseAvailability,
    status: availabilityStatus,
  } = useGetOpenDockWarehouseAvailability()

  function fetchAvailability(date: string) {
    if (stopAppointment?.loadType.id) {
      fetchOpenDockWarehouseAvailability(shipmentUUID, stopIndex, {
        loadTypeId: stopAppointment.loadType.id,
        appointmentDate: date,
      })
    }
  }

  useEffect(() => {
    if (appointmentDate) {
      fetchAvailability(appointmentDate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { values, setFieldValue, errors, touched, setFieldTouched, isValid } = useFormik<{
    dock: { value: string; label: string } | null
    appointmentTime: string | null
  }>({
    initialValues: {
      dock: stopAppointment
        ? { value: stopAppointment.dockId, label: stopAppointment.dockName }
        : null,
      appointmentTime: null,
    },
    onSubmit: () => undefined,
    validationSchema: Schema,
  })

  const currentLoadType = warehouseDetails?.loadTypes.find(
    lt => lt.id === stopAppointment?.loadType.id
  )
  const dockOptions =
    currentLoadType?.docks.map(dock => ({ label: dock.name, value: dock.id })) ?? []

  const timeSlots = getStartTimesFromDocks(docksAvailability ?? [], values.dock?.value)
  const isLoadingAvailability = availabilityStatus === RequestStatus.Pending
  const showScheduleTimeList = Boolean(appointmentDate && availabilityStatus === RequestStatus.Done)

  const hourValidationStatus =
    touched.appointmentTime && errors.appointmentTime ? ('danger' as const) : ('default' as const)

  const disableSubmit = !isValid || !appointmentDate

  return (
    <>
      <Drawer.Header>
        <Layout.Group align="center" gap="spacing-1">
          <Icon name="calendar" size="24px" />
          Reschedule
        </Layout.Group>
        <Drawer.Close />
      </Drawer.Header>
      <Drawer.Body>
        <Layout.Stack gap="spacing-4" paddingB="spacing-2">
          <OpenDockAppointmentSummary
            facilityName={facilityName}
            facilityTimezone={facilityTimezone}
            stopAppointment={stopAppointment}
            betweenLoadTypeAndScheduledAt={
              <Field label="Docks" size="small">
                <Select
                  name="dock"
                  placeholder="Select dock"
                  options={dockOptions}
                  value={values.dock as Selectable | null}
                  onChange={({ target }) => {
                    setFieldValue('dock', target.value as { value: string; label: string } | null)
                    setFieldValue('appointmentTime', null)
                    if (appointmentDate) fetchAvailability(appointmentDate)
                  }}
                  hideClear
                />
              </Field>
            }
          />
          <Divider />
          <Field label="New appointment date" size="small">
            <DatePickerWrapper>
              <DatePicker
                name="appointmentDate"
                value={appointmentDate}
                onChange={({ target }) => {
                  setFieldValue('appointmentTime', null)
                  setAppointmentDate(target.value)
                  if (target.value) fetchAvailability(target.value)
                }}
                constraints={[[null, DateHelper().subtract('day', 1).toString()]]}
              />
            </DatePickerWrapper>
          </Field>
          {isLoadingAvailability && <RectangleSkeleton />}
          {showScheduleTimeList && (
            <ScheduleTimeList
              label={
                <Text variant="body-sm" color="color-text-tertiary">
                  New hour
                </Text>
              }
              timeSlots={timeSlots}
              setValue={(value: string | null) => {
                setFieldTouched('appointmentTime')
                setFieldValue('appointmentTime', value)
              }}
              value={values.appointmentTime}
              validationStatus={hourValidationStatus}
              errorMessage={errors.appointmentTime}
            />
          )}
          <Divider />
          <Layout.Box
            paddingY="spacing-4"
            paddingX="spacing-6"
            borderWidth="border-thin"
            borderRadius="border-radius-m"
            borderColor="color-border-input"
          >
            <Layout.Stack gap="spacing-1">
              <Text variant="body-md-bold">Once you confirm, your changes will be saved</Text>
              {appointmentDate && values.appointmentTime && (
                <Text
                  variant="body-sm"
                  color="color-text-tertiary"
                  style={{ wordBreak: 'break-all' }}
                >
                  {`New schedule: ${formatScheduledAt(
                    appointmentDate,
                    values.appointmentTime,
                    facilityTimezone
                  )}`}
                </Text>
              )}
            </Layout.Stack>
          </Layout.Box>
        </Layout.Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.ActionTertiary
          style={{ marginRight: 'auto' }}
          leading=<Icon name="arrow-left" />
          onClick={onBack}
        >
          Back
        </Drawer.ActionTertiary>
        {/* @TODO: Call the reschedule confirmation API when the endpoint is implemented */}
        <Drawer.ActionPrimary disabled={disableSubmit} onClick={onReschedule}>
          Confirm
        </Drawer.ActionPrimary>
      </Drawer.Actions>
    </>
  )
}
